import { PathOptions } from 'leaflet';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Polygon } from 'react-leaflet';
import loadingContext from '../../context/loadingContext';
import parameterContext from '../../context/parameterContext';
import { ParsedWarningContent, SevereWarnings, Warning } from '../../services/warnings/severeWarnings';
import Modal from '../modal/Modal';
import WarningContent from './WarningContent';
import FallbackContent from './FallbackContent';

export const Warnings: React.FC <{type:string, opacity: number}>= ({ type, opacity }) => {
    const [warnings, setWarnings] = useState<Warning[]>([]);
    const setLoading = useContext(loadingContext);
    const parameters = useContext(parameterContext);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const previousWarningsRef = useRef<Warning[]>([]);
    const [isModalWindowOpen, setIsModalWindowOpen] = useState(false);
    const [currentWarnings, setCurrentWarnings] = useState<ParsedWarningContent | null>(null);
    const [isModalLoading, setIsModalLoading] = useState(false);

    
    let showTornadoWarnings = true;
    if (typeof parameters.showTornadoWarnings !== 'undefined') {
        showTornadoWarnings = parameters.showTornadoWarnings;
    }
    let showSevereThunderstormWarnings = true;
    if (typeof parameters.showSevereThunderstormWarnings !== 'undefined') {
        showSevereThunderstormWarnings = parameters.showSevereThunderstormWarnings;
    }
    let hideSpecialMarines = false;
    if (typeof parameters.hideSpecialMarines !== 'undefined') {
        hideSpecialMarines = parameters.hideSpecialMarines;
    }
    let highlightCriticalWarnings = false;
    if (typeof parameters.highlightCriticalWarnings !== 'undefined') {
        highlightCriticalWarnings = parameters.highlightCriticalWarnings;
    }
    const severeWarnings = new SevereWarnings(showTornadoWarnings, showSevereThunderstormWarnings, hideSpecialMarines, highlightCriticalWarnings);
    
    const init =  async () => {
        setLoading(true);
        const warnings = await severeWarnings.getWarnings(type);
        setWarnings(warnings);
        previousWarningsRef.current = warnings;
        setLoading(false);
    }
    
    useEffect(() => {
        init();

        if (type === 'warning') {
            intervalRef.current = setInterval(async () => {
                const newWarnings = await severeWarnings.getWarnings(type);
                const hasChanges = JSON.stringify(previousWarningsRef.current) !== JSON.stringify(newWarnings);
                if (hasChanges) {
                    previousWarningsRef.current = newWarnings;
                    setWarnings(newWarnings);
                }
            }, 5000);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);

    const handleModalClose = () => {
        setIsModalWindowOpen(false);
        setCurrentWarnings(null);
        setIsModalLoading(false);
    }

    const handlePolygonClick = async (warning: Warning) => {
        if (isModalLoading) return;
        setIsModalWindowOpen(true);
        setIsModalLoading(true);
        try {
            const data = await severeWarnings.getWarningsContent(type, warning.url);
            setCurrentWarnings(data);
        } catch (error) {
            console.error('Error fetching warning content:', error);
        } finally {
            setIsModalLoading(false);
        }
    };

    const createEventHandlers= (warning: Warning) => {
        return {
            click: () => handlePolygonClick(warning),
            keydown: (e: any) => {
                if (e.originalEvent.key === 'Enter' || e.originalEvent.keyCode === 13) {
                    handlePolygonClick(warning)
                }
            }
        }
    };

    return (
        <>

            {
                isModalWindowOpen && currentWarnings?.isFallback && (
                    <Modal
                        title={type === "warning" ? "SEVERE WARNING" : "WARNING REPORT"}
                        isOpen={isModalWindowOpen}
                        onClose={handleModalClose}
                    >
                        <FallbackContent type={type}
                            warning={currentWarnings} isLoading={isModalLoading}
                        />
                    </Modal>
                )
            }
            {
                isModalWindowOpen && !currentWarnings?.isFallback && (
                    <Modal
                        title={type === "warning" ? "SEVERE WARNING" : "WARNING REPORT"}
                        isOpen={isModalWindowOpen}
                        onClose={handleModalClose}
                    >
                        <WarningContent type={type}
                            warning={currentWarnings} isLoading={isModalLoading}
                        />
                    </Modal>
                )
            }
            {
                warnings.map((warning: any, index: number) => {


                    let paneName = 'normalMarkersPane';
                    if(type === 'warning'){
                        paneName = 'nonPriorityMarkersPane';
                    } else if( type === 'normal_warning'){
                        paneName = 'normalMarkersPane';
                    }
                    if(warning.type === 'Tornado'){
                        paneName = 'highPriorityMarkersPane';
                    }else if(warning.type === 'Flash Flood' || warning.type === 'Severe Thunderstorm'){
                        paneName = 'priorityMarkersPane';
                    }

                    const limeOptions: PathOptions = {
                        color: warning.color,
                        fillColor: warning.fillColor === "#ffffff" ? "#6a5acd" : warning.fillColor, // if fillColor is white, change it to light blue
                        opacity: (opacity/100),
                        weight: warning.weight,
                        fillOpacity:(opacity/100)
                    }
                    return (
                        <Polygon key={warning.type+"_"+index} pathOptions={limeOptions} positions={warning.polygons}  eventHandlers={createEventHandlers(warning)} pane={paneName} />
                    );
                })

            }
        </>
    )
}
